import { useEffect, useId, useState } from 'react';
import { Form } from 'react-bootstrap';
import axiosInstance from 'utils/axiosInterceptors';

type CarMakeOption = {
  carMakeId: number;
  name: string;
};

type CarModelOption = {
  carModelId: number;
  name: string;
  carMakeId: number;
  bodyType?: string | null;
};

type ModelSuggestion = {
  carModelId: number;
  name: string;
  bodyType?: string;
};

function sameName(left: string, right: string) {
  return left.trim().localeCompare(right.trim(), undefined, { sensitivity: 'accent' }) === 0;
}

export function CarModelSelect({
  name = 'CarModel',
  value,
  carMake,
  onChange
}: {
  name?: string;
  value?: string;
  carMake?: string;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void;
}) {
  const listId = useId();
  const [makes, setMakes] = useState<CarMakeOption[]>([]);
  const [models, setModels] = useState<CarModelOption[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axiosInstance.get('/api/v1/carMakes?page=1&pageSize=999&orderBy=name'),
      axiosInstance.get('/api/v1/carModels?page=1&pageSize=999&orderBy=name')
    ])
      .then(([makesResponse, modelsResponse]) => {
        if (cancelled) return;
        const makeData = makesResponse.data.data || makesResponse.data;
        const modelData = modelsResponse.data.data || modelsResponse.data;
        setMakes(Array.isArray(makeData) ? makeData : []);
        setModels(Array.isArray(modelData) ? modelData : []);
      })
      .catch(() => {
        if (cancelled) return;
        setMakes([]);
        setModels([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const current = value ?? '';
  const query = current.trim().toLowerCase();
  const makeIds = new Set(
    makes
      .filter(make => sameName(make.name, carMake ?? ''))
      .map(make => make.carMakeId)
  );
  const suggestionsByName = new Map<string, ModelSuggestion>();
  models.forEach(model => {
    const modelName = model.name.trim();
    const key = modelName.toLowerCase();
    if (!modelName || !makeIds.has(model.carMakeId)) return;
    if (query && !key.includes(query)) return;
    const existing = suggestionsByName.get(key);
    if (existing?.bodyType) return;
    suggestionsByName.set(key, {
      carModelId: model.carModelId,
      name: modelName,
      bodyType: model.bodyType || undefined
    });
  });
  const suggestions = [...suggestionsByName.values()];
  const activeIndex = suggestions.length === 0 ? 0 : Math.min(highlight, suggestions.length - 1);

  const emit = (field: string, next: string) => {
    onChange({
      target: { name: field, value: next }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const choose = (suggestion: ModelSuggestion) => {
    emit(name, suggestion.name);
    emit('BodyType', suggestion.bodyType ?? '');
    setOpen(false);
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        Model <span className="text-danger">*</span>
      </Form.Label>
      <div className="position-relative">
        <Form.Control
          type="text"
          name={name}
          value={current}
          required
          autoComplete="off"
          role="combobox"
          aria-label="Model"
          aria-autocomplete="list"
          aria-expanded={open && suggestions.length > 0}
          aria-controls={listId}
          placeholder="Search or enter model"
          onChange={event => {
            onChange(event);
            setOpen(true);
            setHighlight(0);
            const typed = event.target.value.trim().toLowerCase();
            const match = models.find(
              model => makeIds.has(model.carMakeId) && model.name.trim().toLowerCase() === typed
            );
            if (match) emit('BodyType', match.bodyType ?? '');
          }}
          onFocus={() => {
            setOpen(true);
            setHighlight(0);
          }}
          onBlur={() => setOpen(false)}
          onKeyDown={event => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setHighlight(index => Math.min(index + 1, Math.max(suggestions.length - 1, 0)));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setHighlight(index => Math.max(index - 1, 0));
            } else if (event.key === 'Enter' && open && suggestions[activeIndex]) {
              event.preventDefault();
              choose(suggestions[activeIndex]);
            } else if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
        />
        {open && suggestions.length > 0 && (
          <div
            id={listId}
            role="listbox"
            className="position-absolute w-100 bg-body border rounded shadow-sm mt-1"
            style={{ zIndex: 5, maxHeight: 220, overflowY: 'auto' }}
          >
            {suggestions.map((model, index) => (
              <button
                key={model.carModelId}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                className={`dropdown-item${index === activeIndex ? ' active' : ''}`}
                onMouseDown={event => {
                  event.preventDefault();
                  choose(model);
                }}
                onMouseEnter={() => setHighlight(index)}
              >
                {model.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </Form.Group>
  );
}
